import React, { Component, PropTypes } from 'react';
import { reduxForm, Field, SubmissionError } from 'redux-form';
import idx from 'idx';
import ModalDialog from 'common/components/modal/modal_dialog';
import AnimatedButton from 'common/components/animated_button';
import { PackageOptions } from 'pap/components/common/input_fields/package_options';
import Money from 'common/components/money';
import PaymentMethod from 'common/containers/payment_method';
import { getFeatureProjectFields } from 'pap/utils';
import { preventDefault } from 'common/utils';

/**
 * Modal to upgrade project features.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */

let Form = ({error, handleSubmit, submitting, submitFailed, onClose, project, papSteps,
             packageItems, upgradeCost, upgradeItems, validate, valid}) => (
  <form onSubmit={handleSubmit}>

    {Object.keys(papSteps).map((key, i) => {
      const items = packageItems.filter((f) => f.step == key && !f.ignoreOnSettings)

      return (
        <fieldset key={i}>
          <h3>{papSteps[key]}</h3>

          <Field name={key} component={PackageOptions} items={items} projectData={project}
            currentFeatures={project.features} downgradeForbidden={true} validate={validate}/>
        </fieldset>
      )
    })}

    <div className="total-wrapper">
      <div className="total">
        Upgrade Cost:
        <span className="value"> {upgradeCost >= 0? <Money value={upgradeCost}/>: '$TBD'}</span>
      </div>
    </div>

    <div className="modal-actions">
      <AnimatedButton processing={submitting} type="submit" label="Upgrade"
        disabled={!valid || upgradeItems.length === 0} processingLabel="Upgrading..."
        errorMsg={error || submitFailed} successMsg="Done!"/>

      <button className="btn btn-text btn-block" onClick={preventDefault(onClose)}>
        Cancel
      </button>
    </div>
  </form>
)

Form = reduxForm({
  form: "FeaturesModal",
})(Form);


export default class FeaturesModal extends Component {
  constructor(props) {
    super(props)

    this.state = { done: false, showPaymentMethod: false }
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleDone = this.handleDone.bind(this)
    this.togglePaymentMethod = this.togglePaymentMethod.bind(this)
  }

  handleSubmit(formData) {
    const { updateFeaturesRequest, updateProjectRequest, project } = this.props

    /* project fields that can be changed once features are activated */
    const projectUpdates = getFeatureProjectFields(formData, project);

    return updateProjectRequest(project.id, projectUpdates)
      .then(() => updateFeaturesRequest(project.id, project.features, formData, project.billables))
      .then(() => this.setState({ done: true }))
      .catch(res => {
        const status = idx(res, _ => _.data.detail.status);
        /* if the error is related to the payment method being declined, allow user to
           update her payment method */
        if (status == 'processor_declined') {
          this.togglePaymentMethod();
        } else {
          throw new SubmissionError({ _error: 'Failed to upgrade your package. Please try again.' })
        }
      })
  }

  handleDone() {
    /* close modal and fetch updated project */
    this.props.onClose()
    this.props.fetchProject(this.props.project.id)
  }

  togglePaymentMethod() {
    this.setState({ showPaymentMethod: !this.state.showPaymentMethod });
  }

  renderPaymentMethod() {
    return (
      <div>
        <h2>Upgrade failed</h2>
        <p>
          Your project couldn't be upgraded using your current payment method.
          You can update it below before trying again.
        </p>
        <PaymentMethod />
        <div className="modal-actions">
          <button className="btn btn-primary btn-block" onClick={this.togglePaymentMethod}>Done</button>
        </div>
      </div>
    )
  }

  render() {
    const { done, showPaymentMethod } = this.state
    const { upgradeItems, upgradeCost, onClose } = this.props

    return (
      <ModalDialog modalOpen={true} dialogClasses="features-modal"
        contentClasses={showPaymentMethod? "overflow-visible" : undefined}
        closeAction={onClose} modalHeader="Upgrade your project">

        {/* Here we need to hide the form with CSS, otherwise we won't be able
            to show its values in the 'done' section after it's submitted. */}
        <div className={done || showPaymentMethod? "hide":""}>
          <p>Great features you can add to get even more value out of your project.</p>

          <Form {...this.props} onSubmit={this.handleSubmit} />
        </div>

        {showPaymentMethod && this.renderPaymentMethod()}

        {done &&
        <div className="done">
          <h3>
            <i className="fa fa-check-circle"/><br/>Package Updated
          </h3>

          <div>You added the following features:</div>

          <ul>
            {upgradeItems.map((item, i) => <li key={i}>- {item.name}</li>)}
          </ul>

          <p>
            Based on your changes, your credit card will be charged <Money value={upgradeCost}/>.
          </p>

          <div className="modal-actions">
            <button className="btn btn-primary" onClick={this.handleDone}>Done</button>
          </div>
        </div>}

      </ModalDialog>)
  }
}

/**
 * PropTypes.
 *
 * @property {object} project - project
 * @property {object} papSteps - PAP steps for which feature fields should be shown
 * @property {array} packageItems - list of all configurable package items
 * @property {array} upgradeItems - items present in the upgrade
 * @property {number} upgradeCost - upgrade cost
 * @property {func} onClose - callback to close the modal
 * @property {func} updateFeaturesRequest - request to update project features
 * @property {func} updateProjectRequest - request to update project
 * @property {func} fetchProject - fetch project action
 */
FeaturesModal.propTypes = {
  project: PropTypes.object,
  papSteps: PropTypes.object,
  packageItems: PropTypes.array,
  upgradeItems: PropTypes.array,
  upgradeCost: PropTypes.number,
  onClose: PropTypes.func,
  updateFeaturesRequest: PropTypes.func,
  fetchProject: PropTypes.func,
};



// WEBPACK FOOTER //
// ./project/components/features_modal.jsx